import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import useUserTokenPrivileges from '../hooks/useUserTokenPrivileges';

/**
 * A route guard that only renders children if the user is authenticated
 * and has one of the allowed roles. Otherwise, it redirects.
 *
 * @param {string[]} allowedRoles - array of roles permitted to access the route
 * @returns {JSX.Element|null}
 */
const PrivateRoutes = ({ allowedRoles = [] }) => {
  const { user, token, accessPrivileges, isLoading } = useUserTokenPrivileges();
  const location = useLocation();

  // While we fetch user/token info, show a loading indicator
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // If not logged in, redirect to login with return path
  if (!token || !user) {
    return (
      <Navigate to="/login" state={{ from: location }} replace />
    );
  }

  // If the user does not have any of the allowed roles
  if (allowedRoles.length > 0 && !allowedRoles.includes(accessPrivileges)) {
    // Optionally, redirect to unauthorized page or fallback
    return (
      <Navigate to="/login" state={{ from: location }} replace />
    );
  }

  // Authorized: render child routes
  return <Outlet />;
};

export default PrivateRoutes;
